import React, { useState, useEffect } from 'react';
import { Row, Col, Form, InputGroup, Button } from 'react-bootstrap';
import MarketplaceServices from '../services/MarketplaceServices';

const PublicationFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters,
  loading = false 
}) => {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await MarketplaceServices.getCategories({ all: true });
      const categoriesData = response.data?.categories || [];
      
      // Flatten categories for filter dropdown
      const flatCategories = [{ id: 'all', name: 'Todas as Categorias' }];
      categoriesData.forEach(category => {
        flatCategories.push({
          id: category.id,
          name: category.name,
          full_path: category.full_path || category.name
        });
        
        if (category.subcategories && category.subcategories.length > 0) {
          category.subcategories.forEach(subcat => {
            flatCategories.push({
              id: subcat.id,
              name: `${category.name} > ${subcat.name}`,
              full_path: subcat.full_path || `${category.name} > ${subcat.name}`
            });
          });
        }
      });
      
      setCategories(flatCategories);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      setCategories([{ id: 'all', name: 'Todas as Categorias' }]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value,
      page: 1 // Reset to first page when filtering
    });
  };

  const handleClear = () => {
    onClearFilters();
  };

  const statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'published', label: 'Publicado' },
    { value: 'pending', label: 'Aguardando Aprovação' },
    { value: 'suspended', label: 'Suspenso' },
    { value: 'rejected', label: 'Rejeitado' },
    { value: 'removed', label: 'Removido' }
  ];

  const pageSizeOptions = [
    { value: 10, label: '10 por página' },
    { value: 25, label: '25 por página' },
    { value: 50, label: '50 por página' },
    { value: 100, label: '100 por página' }
  ];

  return (
    <div className="marketplace-filters-container mb-4">
      <Row className="g-3">
        {/* Search */}
        <Col lg={3} md={6}>
          <Form.Group>
            <Form.Label className="small text-muted fw-bold">
              BUSCAR
            </Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <i className="las la-search" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Nome do agente ou ID do produto..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                disabled={loading}
              />
              {filters.search && (
                <Button
                  variant="outline-secondary"
                  onClick={() => handleFilterChange('search', '')}
                  disabled={loading}
                >
                  <i className="las la-times" />
                </Button>
              )}
            </InputGroup>
          </Form.Group>
        </Col>

        {/* Category Filter */}
        <Col lg={3} md={6}>
          <Form.Group>
            <Form.Label className="small text-muted fw-bold">
              CATEGORIA
            </Form.Label>
            <Form.Select
              value={filters.category || 'all'}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              disabled={loading || categoriesLoading}
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        {/* Status Filter */}
        <Col lg={2} md={6}>
          <Form.Group>
            <Form.Label className="small text-muted fw-bold">
              STATUS
            </Form.Label>
            <Form.Select
              value={filters.status || 'all'}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              disabled={loading}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        {/* Page Size */}
        <Col lg={2} md={6}>
          <Form.Group>
            <Form.Label className="small text-muted fw-bold">
              ITENS POR PÁGINA
            </Form.Label>
            <Form.Select
              value={filters.page_size || 10}
              onChange={(e) => handleFilterChange('page_size', parseInt(e.target.value))}
              disabled={loading}
            >
              {pageSizeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        {/* Actions */}
        <Col lg={2} md={6} className="d-flex align-items-end">
          <Button
            variant="outline-secondary"
            onClick={handleClear}
            disabled={loading}
            className="w-100"
            title="Limpar filtros"
          >
            <i className="las la-redo-alt me-1" />
            Limpar
          </Button>
        </Col>
      </Row>

      {/* Active Filters Display */}
      {(filters.search || (filters.status && filters.status !== 'all') || (filters.category && filters.category !== 'all')) && (
        <div className="active-filters mt-3">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="small text-muted fw-bold me-2">Filtros ativos:</span>
            
            {filters.search && (
              <span className="badge bg-light text-dark border">
                Busca: "{filters.search}"
                <button
                  type="button"
                  className="btn-close btn-close-sm ms-1"
                  aria-label="Remover filtro de busca"
                  onClick={() => handleFilterChange('search', '')}
                  style={{ fontSize: '0.7em' }}
                ></button>
              </span>
            )}
            
            {filters.category && filters.category !== 'all' && (
              <span className="badge bg-light text-dark border">
                Categoria: {categories.find(cat => cat.id.toString() === filters.category.toString())?.name}
                <button
                  type="button"
                  className="btn-close btn-close-sm ms-1"
                  aria-label="Remover filtro de categoria"
                  onClick={() => handleFilterChange('category', 'all')}
                  style={{ fontSize: '0.7em' }}
                ></button>
              </span>
            )}
            
            {filters.status && filters.status !== 'all' && (
              <span className="badge bg-light text-dark border">
                Status: {statusOptions.find(opt => opt.value === filters.status)?.label}
                <button
                  type="button"
                  className="btn-close btn-close-sm ms-1"
                  aria-label="Remover filtro de status"
                  onClick={() => handleFilterChange('status', 'all')}
                  style={{ fontSize: '0.7em' }}
                ></button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicationFilters;